import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Modal from '../Modal';
import MarkdownRenderer from '../MarkdownRenderer';
import {
  AdminFlagDetail,
  FLAG_REASON_LABELS,
  expertAgentApi,
  expertKeys,
  formatDateTime,
  getExpertErrorMessage,
  isRequestCancelled,
} from '../../lib/domain-expert-api';
import AnswerEditor, { ANSWER_TOO_LONG, answerTooLong } from './AnswerEditor';
import { btnPrimary, btnSecondary, inputClass, labelClass } from './ui';

type Mode = 'view' | 'fix' | 'close';

interface FlagDetailModalProps {
  flagId: string | null;
  onClose: () => void;
  enabled: boolean;
  readOnly: boolean;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
      {children}
    </div>
  );
}

function expertName(flag: AdminFlagDetail) {
  return flag.flagged_by?.name || flag.flagged_by?.email || 'A user';
}

export default function FlagDetailModal({ flagId, onClose, enabled, readOnly }: FlagDetailModalProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('view');
  const [showContext, setShowContext] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: flag, isInitialLoading, isError } = useQuery({
    queryKey: expertKeys.flag(flagId ?? ''),
    queryFn: () => expertAgentApi.getFlag(flagId as string),
    enabled: enabled && !!flagId,
  });

  useEffect(() => {
    setMode('view');
    setShowContext(false);
    setNote('');
    setFormError(null);
  }, [flagId]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['expert', 'flags'] });
    queryClient.invalidateQueries({ queryKey: expertKeys.flagCounts() });
    if (flagId) queryClient.invalidateQueries({ queryKey: expertKeys.flag(flagId) });
  };

  const onError = (err: unknown) => {
    if (isRequestCancelled(err)) return;
    setFormError(getExpertErrorMessage(err));
  };

  const fixMutation = useMutation({
    mutationFn: (body: { question: string; answer: string; note?: string }) => expertAgentApi.fixFlag(flagId as string, body),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['expert', 'answers'] });
      toast.success('Your agent will use this answer going forward.');
      onClose();
    },
    onError,
  });

  const closeMutation = useMutation({
    mutationFn: (body: { note?: string }) => expertAgentApi.closeFlag(flagId as string, body),
    onSuccess: () => {
      invalidate();
      toast.success('Marked as no change needed.');
      onClose();
    },
    onError,
  });

  const reopenMutation = useMutation({
    mutationFn: () => expertAgentApi.reopenFlag(flagId as string),
    onSuccess: () => {
      invalidate();
      toast.success('Moved back to Needs review.');
      onClose();
    },
    onError: (err) => {
      if (isRequestCancelled(err)) return;
      toast.error(getExpertErrorMessage(err));
    },
  });

  const busy = fixMutation.isLoading || closeMutation.isLoading || reopenMutation.isLoading;

  if (!flagId) return null;

  const startFix = () => {
    if (!flag) return;
    setQuestion(flag.question);
    setAnswer(flag.suggested_answer ?? '');
    setNote('');
    setFormError(null);
    setMode('fix');
  };

  const startClose = () => {
    setNote('');
    setFormError(null);
    setMode('close');
  };

  const submitFix = () => {
    const q = question.trim();
    const a = answer.trim();
    if (!q || !a) {
      setFormError('Enter the question and the corrected answer.');
      return;
    }
    if (answerTooLong(a)) {
      setFormError(ANSWER_TOO_LONG);
      return;
    }
    fixMutation.mutate({ question: q, answer: a, ...(note.trim() ? { note: note.trim() } : {}) });
  };

  const submitClose = () => {
    closeMutation.mutate(note.trim() ? { note: note.trim() } : {});
  };

  const isOpenFlag = flag?.status === 'open';
  const isDone = flag?.status === 'resolved' || flag?.status === 'dismissed';

  let footer: React.ReactNode;
  if (mode === 'fix') {
    footer = (
      <>
        <button type="button" onClick={() => setMode('view')} disabled={busy} className={`${btnSecondary} mt-3 sm:mt-0`}>
          Back
        </button>
        <button type="button" onClick={submitFix} disabled={busy || readOnly} className={btnPrimary}>
          {fixMutation.isLoading ? 'Saving…' : 'Save answer'}
        </button>
      </>
    );
  } else if (mode === 'close') {
    footer = (
      <>
        <button type="button" onClick={() => setMode('view')} disabled={busy} className={`${btnSecondary} mt-3 sm:mt-0`}>
          Back
        </button>
        <button type="button" onClick={submitClose} disabled={busy || readOnly} className={btnPrimary}>
          {closeMutation.isLoading ? 'Saving…' : 'No change needed'}
        </button>
      </>
    );
  } else {
    footer = (
      <>
        <button type="button" onClick={onClose} className={`${btnSecondary} mt-3 sm:mt-0`}>
          Close
        </button>
        {isOpenFlag && !readOnly && (
          <>
            <button type="button" onClick={startClose} disabled={busy} className={`${btnSecondary} mt-3 sm:mt-0`}>
              No change needed
            </button>
            <button type="button" onClick={startFix} disabled={busy} className={btnPrimary}>
              Fix answer
            </button>
          </>
        )}
        {isDone && !readOnly && (
          <button type="button" onClick={() => reopenMutation.mutate()} disabled={busy} className={btnSecondary}>
            {reopenMutation.isLoading ? 'Reopening…' : 'Reopen'}
          </button>
        )}
      </>
    );
  }

  const title = mode === 'fix' ? 'Fix answer' : mode === 'close' ? 'No change needed' : 'Flagged answer';

  return (
    <Modal isOpen onClose={busy ? () => undefined : onClose} title={title} size="md" footer={footer}>
      {isInitialLoading ? (
        <p className="py-8 text-center text-sm text-gray-500">Loading…</p>
      ) : isError || !flag ? (
        <p className="py-8 text-center text-sm text-red-600">We couldn't load this flagged answer. Please try again.</p>
      ) : mode === 'fix' ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Save the correct answer. When a user asks a question like this, your agent will give this exact answer.
          </p>
          <div>
            <label htmlFor="fix-question" className={labelClass}>
              Question
            </label>
            <input
              id="fix-question"
              type="text"
              value={question}
              maxLength={1000}
              onChange={(e) => setQuestion(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <span className={labelClass}>Corrected answer</span>
            <AnswerEditor value={answer} onChange={setAnswer} label="Corrected answer" />
          </div>
          <div>
            <label htmlFor="fix-note" className={labelClass}>
              Note to the user <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea id="fix-note" rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} className={inputClass} />
          </div>
          {formError && <p className="text-sm text-red-600" role="alert">{formError}</p>}
        </div>
      ) : mode === 'close' ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">The answer stays as it is. You can let the user know why.</p>
          <div>
            <label htmlFor="close-note" className={labelClass}>
              Note to the user <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="close-note"
              rows={3}
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              className={inputClass}
            />
          </div>
          {formError && <p className="text-sm text-red-600" role="alert">{formError}</p>}
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-xs text-gray-500">
            {FLAG_REASON_LABELS[flag.reason_category] ?? 'Other'} · Flagged by {expertName(flag)} on {formatDateTime(flag.created_at)}
          </p>

          <Section title="Question">
            <p className="whitespace-pre-wrap text-sm text-gray-900">{flag.question}</p>
          </Section>

          <Section title="Agent's answer">
            <div className="border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
              <MarkdownRenderer content={flag.answer || ''} />
            </div>
          </Section>

          {flag.comment && (
            <Section title="User's comment">
              <p className="whitespace-pre-wrap text-sm text-gray-800">{flag.comment}</p>
            </Section>
          )}

          {flag.suggested_answer && (
            <Section title="Suggested answer">
              <p className="whitespace-pre-wrap border border-indigo-100 bg-indigo-50 p-3 text-sm text-gray-800">
                {flag.suggested_answer}
              </p>
            </Section>
          )}

          {isDone && (
            <div className="border border-gray-200 p-3 text-sm">
              <p className="font-medium text-gray-900">{flag.status === 'resolved' ? 'Fixed' : 'No change needed'}</p>
              {flag.resolution_note && <p className="mt-1 whitespace-pre-wrap text-gray-700">{flag.resolution_note}</p>}
              <p className="mt-1 text-xs text-gray-500">
                {flag.resolved_by_email ? `By ${flag.resolved_by_email} · ` : ''}
                {formatDateTime(flag.resolved_at)}
              </p>
            </div>
          )}

          {flag.context && flag.context.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowContext((prev) => !prev)}
                aria-expanded={showContext}
                className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                {showContext ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {showContext ? 'Hide conversation' : 'Show conversation'}
              </button>
              {showContext && (
                <div className="mt-3 space-y-3">
                  {flag.context.map((message, index) => (
                    <div
                      key={`${message.created_at}-${index}`}
                      className={`border p-3 text-sm ${message.role === 'user' ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <p className="mb-1 text-xs font-semibold text-gray-500">{message.role === 'user' ? 'User' : 'Agent'}</p>
                      {message.role === 'assistant' ? (
                        <MarkdownRenderer content={message.content || ''} />
                      ) : (
                        <p className="whitespace-pre-wrap text-gray-800">{message.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
